<script setup lang="ts">
import { Planungsgeschaeft } from '@/models/Planungsgeschaeft';
import { inject, nextTick, onMounted, reactive, ref, watch, type Ref } from 'vue';
import {
    RouterLink,
    RouterView,
    onBeforeRouteLeave,
    onBeforeRouteUpdate,
    useRoute,
    useRouter,
    type RouteLocationNormalized
} from 'vue-router';
import { instance as api } from '@/helpers/api';
import { sectionNameToRoute } from '@/helpers/planungsgeschaeft';
import { hasPpaReachedSubmitRoute } from '@/helpers/tools';

const router = useRouter();
const route = useRoute();
const scrollanchor: Ref<HTMLElement | null> = ref(null);

const childcomponent: Ref<any> = ref(null);

// injected from PlanungsgeschaeftEditView
const saveGeschaeft = inject('saveGeschaeft') as () => Promise<void>;
const saving = inject('saving') as Ref<boolean>;

const props = defineProps<{
    geschaeft: Planungsgeschaeft;
}>();

const emit = defineEmits<{
    'geschaeft-changed': [section: string | null];
}>();

function navItemiconColor(item: NavItem) {
    if (item.sectionName && hasPpaReachedSubmitRoute(props.geschaeft)) {
        return item.isValid ? 'default' : 'danger';
    } else {
        return 'default';
    }
}

type NavItem = {
    titleI18nKey: string;
    route?: { name: string };
    // the section name is used to call checkValidity in the backend:
    sectionName: string | null;
    isValid: boolean;
    isVisible: boolean;
    visibilityCheckFunction?: () => boolean;
};
type NavGroup = {
    titleI18nKey: string;
    expanded?: boolean;
    items: NavItem[];
};

/**
 * The navigation tree is reactive, so that we can dynamically change
 * e.g. the validity if validity check information is present:
 */
const navGroups = reactive({
    allgemeine_angaben: {
        titleI18nKey: 'allgemeine_information',
        expanded: true,
        items: [
            {
                titleI18nKey: 'allgemeine_angaben',
                sectionName: 'allgemeine_angaben',
                route: sectionNameToRoute('allgemeine_angaben'),
                isVisible: true,
                isValid: false
            },
            {
                titleI18nKey: 'beteiligte',
                sectionName: 'beteiligte',
                route: sectionNameToRoute('beteiligte'),
                isVisible: true,
                isValid: false
            }
        ]
    },
    geschaeftsunterlagen: {
        titleI18nKey: 'geschaeftsunterlagen',
        expanded: true,
        items: [
            {
                titleI18nKey: 'berichte_plaene_vorschriften',
                sectionName: null,
                route: sectionNameToRoute('geschaeftsunterlagen'),
                isVisible: true,
                isValid: true
            },
            {
                titleI18nKey: 'weitere_unterlagen',
                sectionName: null,
                route: sectionNameToRoute('weitere_unterlagen'),
                isVisible: true,
                isValid: true
            }
        ]
    },
    abschluss: {
        titleI18nKey: 'abschluss',
        expanded: true,
        items: [
            {
                titleI18nKey: 'anmerkungen',
                route: { name: 'planungsgeschaeft-edit-anmerkungen' },
                isVisible: true,
                isValid: false
            },
            {
                titleI18nKey: 'commit',
                sectionName: null,
                route: sectionNameToRoute('abschluss'),
                isVisible: true,
                isValid: true
            }
        ]
    }
} as { [key: string]: NavGroup });

// On mount and geschaeft change, we re-load the validity check information and
// make sure the sections have their visibility set correctly.
onMounted(() => {
    loadAllValidityChecks();
    updateSectionVisibility();
});

// If the Geschaeft changes, e.g. due to a routing change, we re-load the validity check information:
watch(() => props.geschaeft, loadAllValidityChecks);

// Watch for a Geschaeft save end event: we watch saving, and if it toggles
// from true to false, we know that a saving was in place and is now done.
// We then update the section visibility according to the new state.
watch(saving, (newVal, oldVal) => {
    if (newVal !== true && oldVal === true) {
        updateSectionVisibility();
    }
});

/**
 * Check  / set visibility of each section, depending on the actual geschaefts- or additional data:
 * watchEffect watches the reactive values of geschaeft and additional data, and sets the visibility accordingly.
 */
async function updateSectionVisibility() {
    // get all sections that are visible depending on some check condition here:
    const sectionsToCheck = getNavigatableNavItems().filter(
        (item) => item.sectionName && item.visibilityCheckFunction instanceof Function
    );

    // now, show/hide the sections based on the results above, and initiate
    // a validity check for each section when visible:
    const sectionsToValidate: string[] = [];
    sectionsToCheck.forEach((section) => {
        if (section.visibilityCheckFunction && section.sectionName) {
            const sectionIsVisible = section.visibilityCheckFunction();
            setVisibilityForSection(section.sectionName, sectionIsVisible);
            if (sectionIsVisible) {
                sectionsToValidate.push(section.sectionName);
            }
        }
    });
    loadValidityChecks(sectionsToValidate);
}

onBeforeRouteUpdate(async (to, fro) => {
    try {
        await navigationHandler(to, fro);
    } catch (e) {
        window.alert(e);
        console.error(e);
        return false;
    }
});

onBeforeRouteLeave(async (to, fro) => {
    try {
        await navigationHandler(to, fro);
    } catch (e) {
        console.error(e);
        window.alert(e);
        return false;
    }
});

async function navigationHandler(to: RouteLocationNormalized, from: RouteLocationNormalized) {
    // 1. call child components onBeforeSave
    // 2. save Geschaeft before navigating
    // 3. call child components onAfterSave
    // 4. execute specific validation check from OLD route

    if (childcomponent.value?.onBeforeSave) {
        await childcomponent.value.onBeforeSave();
    }

    await saveGeschaeft();

    if (childcomponent.value?.onAfterSave instanceof Function) {
        await childcomponent.value.onAfterSave();
    }

    // find section of old route, and execute a Geschaeft changed
    // event:
    let sectionName = null;
    const items = getNavigatableNavItems();
    const oldNavItem = items.find((item) => item.route?.name === from.name);
    if (oldNavItem?.sectionName) {
        sectionName = oldNavItem.sectionName;
    }
    // Inform other components that the geschaeft has changed:
    // This will update the validity checks, and inform the parent:
    onGeschaeftChanged(sectionName);

    if (to.meta?.allowScrollAnchors && scrollanchor.value) {
        await nextTick();
        // Firefox benötigt zusätzliche Zeit für DOM-Rendering
        setTimeout(() => {
            if (scrollanchor.value) {
                const element = scrollanchor.value;
                const scrollMargin = 320; // entspricht scroll-margin-top aus CSS
                const rect = element.getBoundingClientRect();
                const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
                const offsetPosition = rect.top + scrollTop - scrollMargin;

                window.scrollTo({
                    top: offsetPosition,
                    behavior: 'smooth'
                });
            }
        }, 100);
    }
}

/**
 * Loads the validity info for the actual Geschaeft from the backend,
 * and updates the section info (navGroups items) with the validity.
 * This is used to indicate if a section is valid or not.
 * @param sections
 */
async function loadValidityChecks(sections: string[]) {
    try {
        const res = await api().post('/Planungsgeschaeft/checkValidity', {
            id: props.geschaeft.id,
            sections: sections
        });
        if (res.data?.sections) {
            for (const sectionName in res.data.sections) {
                const sectionInfo = res.data.sections[sectionName];
                setValidityForSection(sectionName, sectionInfo.valid === true);
            }
        }
    } catch (e) {
        console.error(e);
    } finally {
        // pass
    }
}

async function loadAllValidityChecks() {
    const sections = getValidityCheckSectionNames(navGroups);
    await loadValidityChecks(sections);
}

function getValidityCheckSectionNames(navGroups: { [key: string]: NavGroup }): string[] {
    const sections: string[] = [];
    Object.values(navGroups).forEach((group) => {
        group.items.forEach((item) => {
            if (item.sectionName && item.isVisible) {
                sections.push(item.sectionName);
            }
        });
    });
    return sections;
}

function getNavigatableNavItems(): NavItem[] {
    const items: NavItem[] = [];
    Object.values(navGroups).forEach((group) => {
        group.items.forEach((item) => {
            if (item.route) {
                items.push(item);
            }
        });
    });
    return items;
}

function setValidityForSection(sectionName: string, isValid: boolean) {
    const groupNames = Object.keys(navGroups);
    for (const groupName of groupNames) {
        navGroups[groupName].items.forEach((item) => {
            if (item.sectionName === sectionName) {
                item.isValid = isValid;
            }
        });
    }
}

function setVisibilityForSection(sectionName: string, isVisible: boolean) {
    const groupNames = Object.keys(navGroups);
    for (const groupName of groupNames) {
        navGroups[groupName].items.forEach((item) => {
            if (item.sectionName === sectionName) {
                item.isVisible = isVisible;
            }
        });
    }
}

function onGeschaeftChanged(section: string | null) {
    if (section) {
        loadValidityChecks([section]);
    } else {
        loadAllValidityChecks();
    }
    emit('geschaeft-changed', section);
}

function navItemicon(item: NavItem) {
    if (item.sectionName) {
        return item.isValid ? 'check' : 'warning';
    } else {
        return '';
    }
}

function onNextBtn() {
    // Find actual nav item entry from the actual route:
    const items = getNavigatableNavItems().filter((item) => item.isVisible);
    const oldNavItem = items.find((item) => item.route?.name === route.name);

    if (!oldNavItem) {
        return;
    }

    // find next entry in the nav items:
    const oldItemIndex = items.indexOf(oldNavItem);
    const nextItem = items[oldItemIndex + 1];

    // Navigate to the next item:
    if (nextItem?.route) {
        router.push(nextItem.route);
    }
}

function onPrevBtn() {
    // Find actual nav item entry from the actual route:
    const items = getNavigatableNavItems().filter((item) => item.isVisible);
    const oldNavItem = items.find((item) => item.route?.name === route.name);

    if (!oldNavItem) {
        return;
    }

    // find next entry in the nav items:
    const oldItemIndex = items.indexOf(oldNavItem);
    const nextItem = items[oldItemIndex - 1];

    // Navigate to the next item:
    if (nextItem?.route) {
        router.push(nextItem.route);
    } else {
        router.push({ name: 'planungsgeschaeft-edit-dashboard', params: { id: props.geschaeft.id } });
    }
}
</script>

<template>
    <div class="layout">
        <nav class="nav-menu">
            <h4 ref="scrollanchor" class="scrollanchor ktg-title-04 mb-1_5rem px-1rem">
                {{ $t('angaben_bearbeiten') }}
            </h4>

            <ktg-cdk-accordion>
                <ktg-cdk-accordion-item
                    v-for="group in navGroups"
                    :key="group.titleI18nKey"
                    :expanded="group.expanded || null"
                    @collapse="group.expanded = false"
                    @expand="group.expanded = true"
                >
                    <div class="nav-header" slot="header">
                        <span> {{ $t(group.titleI18nKey) }}</span>
                        <ktg-icon
                            name="dropdown-arrow-down-small"
                            :style="{
                                transform: group.expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                                transition: 'transform 0.2s'
                            }"
                        ></ktg-icon>
                    </div>
                    <div class="nav-item">
                        <template v-for="item in group.items.filter((i) => i.isVisible)" :key="item.titleI18nKey">
                            <RouterLink
                                v-if="item.route"
                                :to="item.route"
                                custom
                                v-slot="{ href, navigate, isActive, isExactActive }"
                            >
                                <ktg-vertical-navigation-item
                                    :href="href"
                                    :icon="navItemicon(item)"
                                    @click.prevent="navigate()"
                                    :selected="isActive || isExactActive"
                                    :icon-appearance="navItemiconColor(item)"
                                >
                                    {{ $t(item.titleI18nKey) }}
                                </ktg-vertical-navigation-item>
                            </RouterLink>
                            <ktg-vertical-navigation-item v-else>
                                {{ $t(item.titleI18nKey) }}
                            </ktg-vertical-navigation-item>
                        </template>
                    </div>
                </ktg-cdk-accordion-item>
            </ktg-cdk-accordion>
        </nav>

        <RouterView v-slot="{ Component }" class="content">
            <component
                ref="childcomponent"
                :is="Component"
                :geschaeft="props.geschaeft"
                @next="onNextBtn"
                @prev="onPrevBtn"
            />
        </RouterView>
    </div>
</template>

<style scoped lang="scss">
.layout {
    display: grid;
    grid-template-columns: 390px 810px;
    grid-template-areas: 'nav content';
    gap: 5em;
    .nav-menu {
        grid-area: nav;
    }
    .nav-header {
        background-color: var(--ktg-color-neutral-01);
        display: flex;
        justify-content: space-between;
        align-items: center;
        width: 100%;
        padding-top: 0.9375rem;
        padding-bottom: 0.9375rem;
        padding-left: 1rem;
        padding-right: 1rem;
        text-align: left;
        cursor: pointer;
    }
    .nav-item {
        padding-right: 0.5rem;
    }
    .content {
        grid-area: content;
    }
}
.scrollanchor {
    scroll-margin-top: 320px;
}
</style>
